import React, { useState } from "react";
import {
    Box,
    Typography,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableContainer,
    TextField,
    Paper,
    useTheme,
    useMediaQuery,
    Chip,
    Divider,
    Stack,
    Card,
    CardContent,
    Grid,
    IconButton,
    Collapse,
    Fade,
    Slide
} from "@mui/material";
import {
    Psychology as PsychologyIcon,
    School as SchoolIcon,
    Assessment as AssessmentIcon,
    Comment as CommentIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    TrendingUp as TrendingUpIcon,
    Info as InfoIcon
} from "@mui/icons-material";

const UnitDeepDive = ({ unitData }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [expanded, setExpanded] = useState(false);

    if (!unitData || !unitData.entries) {
        return null;
    }

    const dokLevels = unitData.entries.map((e) => parseInt(e.dok_level));
    const maxDOK = Math.max(...dokLevels);
    const highestEntry = unitData.entries.find((e) => parseInt(e.dok_level) === maxDOK);

    // Calculate highest achieved DOK level
    const achievedEntries = unitData.entries.filter((e) => e.is_achieved);
    const achievedDokLevels = achievedEntries.map((e) => parseInt(e.dok_level));
    const maxAchievedDOK = achievedDokLevels.length > 0 ? Math.max(...achievedDokLevels) : 0;
    const highestAchievedEntry = achievedEntries.find((e) => parseInt(e.dok_level) === maxAchievedDOK);

    const getDOKLevelColor = (level) => {
        const colors = {
            1: '#F44336',
            2: '#FF9800',
            3: '#2196F3',
            4: '#4CAF50',
        };
        return colors[level] || theme.palette.primary.main;
    };

    const getDOKDescription = (level) => {
        const descriptions = {
            1: 'Recall',
            2: 'Skill/Concept',
            3: 'Strategic Thinking',
            4: 'Extended Thinking'
        };
        return descriptions[level] || 'Unknown';
    };

    return (
        <Slide direction="up" in timeout={600}>
            <Paper
                elevation={0}
                sx={{
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.divider}`,
                    background: `linear-gradient(135deg, ${theme.palette.primary.light}08, ${theme.palette.secondary.light}12)`,
                    overflow: 'hidden'
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        p: { xs: 3, sm: 4 },
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}15, ${theme.palette.primary.light}25)`,
                        borderBottom: `1px solid ${theme.palette.divider}`
                    }}
                >
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        alignItems={{ xs: 'center', sm: 'flex-start' }}
                        spacing={{ xs: 1, sm: 2 }}
                        sx={{ mb: 2, textAlign: { xs: 'center', sm: 'left' } }}
                    >
                        <PsychologyIcon sx={{
                            color: theme.palette.primary.main,
                            fontSize: { xs: 24, sm: 28 }
                        }} />
                        <Typography
                            variant="h5"
                            fontWeight="bold"
                            color="primary"
                            sx={{
                                fontSize: { xs: '1.3rem', sm: '1.5rem' },
                                wordWrap: 'break-word',
                                overflowWrap: 'break-word'
                            }}
                        >
                            Unit Detail View – Deep Dive
                        </Typography>
                    </Stack>

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Card
                                variant="outlined"
                                sx={{
                                    border: `2px solid ${theme.palette.primary.light}`,
                                    borderRadius: 2,
                                    background: theme.palette.background.paper,
                                    minHeight: { xs: 100, sm: 120 },
                                    display: 'flex',
                                    flexDirection: 'column'
                                }}
                            >
                                <CardContent sx={{
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    p: { xs: 2, sm: 3 }
                                }}>
                                    <Stack
                                        direction={{ xs: 'column', sm: 'row' }}
                                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                                        spacing={1}
                                        sx={{ textAlign: { xs: 'center', sm: 'left' } }}
                                    >
                                        <SchoolIcon sx={{
                                            color: theme.palette.primary.main,
                                            fontSize: { xs: 18, sm: 20 },
                                            alignSelf: { xs: 'center', sm: 'flex-start' }
                                        }} />
                                        <Typography
                                            variant="subtitle1"
                                            fontWeight="bold"
                                            sx={{
                                                fontSize: { xs: '0.9rem', sm: '1rem' },
                                                wordWrap: 'break-word',
                                                overflowWrap: 'break-word'
                                            }}
                                        >
                                            Unit: <strong>{unitData.unit}</strong>
                                        </Typography>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Card
                                variant="outlined"
                                sx={{
                                    border: `2px solid ${getDOKLevelColor(maxDOK)}`,
                                    borderRadius: 2,
                                    background: theme.palette.background.paper,
                                    minHeight: { xs: 100, sm: 120 },
                                    display: 'flex',
                                    flexDirection: 'column'
                                }}
                            >
                                <CardContent sx={{
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between',
                                    p: { xs: 2, sm: 3 }
                                }}>
                                    <Stack
                                        direction={{ xs: 'column', sm: 'row' }}
                                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                                        spacing={1}
                                        sx={{ textAlign: { xs: 'center', sm: 'left' } }}
                                    >
                                        <TrendingUpIcon sx={{
                                            color: getDOKLevelColor(maxDOK),
                                            fontSize: { xs: 18, sm: 20 },
                                            alignSelf: { xs: 'center', sm: 'flex-start' }
                                        }} />
                                        <Typography
                                            variant="subtitle1"
                                            fontWeight="bold"
                                            sx={{
                                                fontSize: { xs: '0.9rem', sm: '1rem' },
                                                wordWrap: 'break-word',
                                                overflowWrap: 'break-word'
                                            }}
                                        >
                                            {maxAchievedDOK > 0 ? (
                                                <>Highest DOK Level Achieved: <strong>DOK {maxAchievedDOK} – {getDOKDescription(maxAchievedDOK)}</strong></>
                                            ) : (
                                                <>No DOK Levels Achieved Yet</>
                                            )}
                                        </Typography>
                                    </Stack>
                                    <Chip
                                        label={maxAchievedDOK > 0 ? `DOK ${maxAchievedDOK} - ${getDOKDescription(maxAchievedDOK)}` : 'No Achievement'}
                                        sx={{
                                            backgroundColor: maxAchievedDOK > 0 ? getDOKLevelColor(maxAchievedDOK) + '20' : theme.palette.grey[200],
                                            color: maxAchievedDOK > 0 ? getDOKLevelColor(maxAchievedDOK) : theme.palette.grey[600],
                                            fontWeight: 'bold',
                                            fontSize: { xs: '0.8rem', sm: '0.9rem' },
                                            alignSelf: { xs: 'center', sm: 'flex-start' },
                                            mt: { xs: 1, sm: 0 }
                                        }}
                                    />
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Box>

                {/* Content */}
                <Box sx={{ p: { xs: 3, sm: 4 } }}>
                    {/* DOK Learning Table Section */}
                    <Box sx={{ mb: 4 }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                            <AssessmentIcon sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
                            <Typography variant="h6" fontWeight="bold" color="primary">
                                Full DOK Learning Table for This Unit:
                            </Typography>
                        </Stack>

                        {/* Desktop/Tablet Table View */}
                        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                            <Paper
                                elevation={0}
                                sx={{
                                    borderRadius: 2,
                                    border: `1px solid ${theme.palette.divider}`,
                                    overflow: 'hidden'
                                }}
                            >
                                <TableContainer>
                                    <Table>
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: theme.palette.primary.light + '15' }}>
                                                <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem', py: 2 }}>
                                                    <Stack direction="row" alignItems="center" spacing={1}>
                                                        <InfoIcon sx={{ fontSize: 18 }} />
                                                        DOK Skills
                                                    </Stack>
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem', py: 2 }}>
                                                    <Stack direction="row" alignItems="center" spacing={1}>
                                                        <AssessmentIcon sx={{ fontSize: 18 }} />
                                                        Achieved?
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {unitData.entries.map((entry, idx) => (
                                                <Fade in timeout={300 + idx * 100} key={idx}>
                                                    <TableRow
                                                        sx={{
                                                            '&:hover': {
                                                                backgroundColor: theme.palette.action.hover,
                                                                transition: 'all 0.2s ease'
                                                            },
                                                            transition: 'all 0.2s ease'
                                                        }}
                                                    >
                                                        <TableCell sx={{ py: 2 }}>
                                                            <Stack direction="row" alignItems="center" spacing={2}>
                                                                <Chip
                                                                    label={`DOK ${entry.dok_level}`}
                                                                    size="small"
                                                                    sx={{
                                                                        backgroundColor: getDOKLevelColor(entry.dok_level) + '20',
                                                                        color: getDOKLevelColor(entry.dok_level),
                                                                        fontWeight: 'bold',
                                                                        fontSize: '0.8rem'
                                                                    }}
                                                                />
                                                                <Typography variant="body2" sx={{ flex: 1 }}>
                                                                    {entry.skill}
                                                                </Typography>
                                                            </Stack>
                                                        </TableCell>
                                                        <TableCell sx={{ py: 2 }}>
                                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                                {entry.is_achieved ? (
                                                                    <>
                                                                        <CheckCircleIcon sx={{ color: theme.palette.success.main, fontSize: 20 }} />
                                                                        <Chip
                                                                            label="Yes"
                                                                            size="small"
                                                                            sx={{
                                                                                backgroundColor: theme.palette.success.main + '20',
                                                                                color: theme.palette.success.main,
                                                                                fontWeight: 'bold',
                                                                                fontSize: '0.8rem'
                                                                            }}
                                                                        />
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <CancelIcon sx={{ color: theme.palette.error.main, fontSize: 20 }} />
                                                                        <Chip
                                                                            label="Not Yet"
                                                                            size="small"
                                                                            sx={{
                                                                                backgroundColor: theme.palette.error.main + '20',
                                                                                color: theme.palette.error.main,
                                                                                fontWeight: 'bold',
                                                                                fontSize: '0.8rem'
                                                                            }}
                                                                        />
                                                                    </>
                                                                )}
                                                            </Stack>
                                                        </TableCell>
                                                    </TableRow>
                                                </Fade>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </Box>

                        {/* Mobile Card View */}
                        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                            <Grid container spacing={2}>
                                {unitData.entries.map((entry, idx) => (
                                    <Fade in timeout={300 + idx * 100} key={idx}>
                                        <Grid item xs={12}>
                                            <Card
                                                variant="outlined"
                                                sx={{
                                                    border: `2px solid ${getDOKLevelColor(entry.dok_level)}`,
                                                    borderRadius: 2,
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                                    transition: 'all 0.3s ease',
                                                    '&:hover': {
                                                        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                                                        transform: 'translateY(-1px)'
                                                    }
                                                }}
                                            >
                                                <CardContent sx={{ p: 3 }}>
                                                    {/* DOK Level Header */}
                                                    <Box sx={{ mb: 2, textAlign: 'center' }}>
                                                        <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ mb: 1 }}>
                                                            <InfoIcon sx={{ color: getDOKLevelColor(entry.dok_level), fontSize: 20 }} />
                                                            <Chip
                                                                label={`DOK ${entry.dok_level}`}
                                                                sx={{
                                                                    backgroundColor: getDOKLevelColor(entry.dok_level) + '20',
                                                                    color: getDOKLevelColor(entry.dok_level),
                                                                    fontWeight: 'bold',
                                                                    fontSize: '1rem',
                                                                    py: 1
                                                                }}
                                                            />
                                                        </Stack>
                                                    </Box>

                                                    {/* Skill Description */}
                                                    <Box sx={{ mb: 3 }}>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                textAlign: 'left',
                                                                wordWrap: 'break-word',
                                                                overflowWrap: 'break-word',
                                                                lineHeight: 1.6,
                                                                fontSize: '0.95rem'
                                                            }}
                                                        >
                                                            {entry.skill}
                                                        </Typography>
                                                    </Box>

                                                    {/* Achievement Status */}
                                                    <Box sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        p: 2,
                                                        borderRadius: 1,
                                                        backgroundColor: entry.is_achieved
                                                            ? theme.palette.success.main + '10'
                                                            : theme.palette.error.main + '10'
                                                    }}>
                                                        <Stack direction="row" alignItems="center" spacing={2}>
                                                            {entry.is_achieved ? (
                                                                <>
                                                                    <CheckCircleIcon sx={{
                                                                        color: theme.palette.success.main,
                                                                        fontSize: 24
                                                                    }} />
                                                                    <Chip
                                                                        label="✅ Achieved"
                                                                        sx={{
                                                                            backgroundColor: theme.palette.success.main + '20',
                                                                            color: theme.palette.success.main,
                                                                            fontWeight: 'bold',
                                                                            fontSize: '1rem',
                                                                            py: 1
                                                                        }}
                                                                    />
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <CancelIcon sx={{
                                                                        color: theme.palette.error.main,
                                                                        fontSize: 24
                                                                    }} />
                                                                    <Chip
                                                                        label="❌ Not Yet"
                                                                        sx={{
                                                                            backgroundColor: theme.palette.error.main + '20',
                                                                            color: theme.palette.error.main,
                                                                            fontWeight: 'bold',
                                                                            fontSize: '1rem',
                                                                            py: 1
                                                                        }}
                                                                    />
                                                                </>
                                                            )}
                                                        </Stack>
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    </Fade>
                                ))}
                            </Grid>
                        </Box>
                    </Box>

                    {/* Teacher Comment Section */}
                    <Box>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                            <CommentIcon sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
                            <Typography variant="h6" fontWeight="bold" color="primary">
                                Teacher Comment:
                            </Typography>
                        </Stack>

                        <Card
                            variant="outlined"
                            sx={{
                                border: `2px solid ${theme.palette.primary.light}`,
                                borderRadius: 2,
                                background: theme.palette.background.paper,
                                minHeight: 120
                            }}
                        >
                            <CardContent>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    value={unitData.educator_comment?.trim() || "No comment"}
                                    InputProps={{
                                        readOnly: true,
                                        sx: {
                                            fontSize: '1rem',
                                            lineHeight: 1.6,
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                border: 'none'
                                            }
                                        }
                                    }}
                                    variant="outlined"
                                    sx={{
                                        '& .MuiInputBase-root': {
                                            backgroundColor: 'transparent'
                                        }
                                    }}
                                />
                            </CardContent>
                        </Card>
                    </Box>
                </Box>
            </Paper>
        </Slide>
    );
};

export default UnitDeepDive;
